import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { IconBrandYoutube, IconSearch, IconMail } from "@tabler/icons-react";
import CustomButton from "../utils/customButton";
import Functions from "../../callable/functions";
import { youtube_v3 } from "googleapis";
import { UserContext } from "../../contexts/userContext";
import { extractVideoId } from "../../utils/youtubeUtils";
import VideoDetailsCard from "../insighter/VideoDetailsCard";
import SentimentAnalysisPanel from "../insighter/SentimentAnalysisPanel";
import AudienceInsightsPanel from "../insighter/AudienceInsightsPanel";
import { Lead } from "../../models/lead";
import moment from "moment";
import LeadDb from "../../db/leadDb";
import { addDoc, getDocs, query, where } from "firebase/firestore";

export default function VideoInsighter() {
  const user = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [videoUrl, setVideoUrl] = useState("");
  const [videoId, setVideoId] = useState<string | null>(
    searchParams.get("videoId")
  );
  const [video, setVideo] = useState<youtube_v3.Schema$Video | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzingSentiment, setAnalyzingSentiment] = useState(false);
  const [sentimentData, setSentimentData] = useState<any | null>(null);
  const [analyzingAudience, setAnalyzingAudience] = useState(false);
  const [audienceInsights, setAudienceInsights] = useState<string | null>(null);

  // Use two separate states:
  // leadEmailInput holds what the user is typing into the email field,
  // leadSubmitted tracks whether the email has been submitted.
  const [leadEmailInput, setLeadEmailInput] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const pageTitle =
    "YouTube Video Insighter | Free YouTube Video Analysis Tool";
  const pageDescription =
    "Analyze any YouTube video to get insights on its performance metrics, audience sentiment, and viewer engagement.";

  useEffect(() => {
    if (videoId) {
      handleFetchVideo(videoId);
    }
  }, [videoId]);

  const handleUrlSubmit = () => {
    const extractedId = extractVideoId(videoUrl);
    if (extractedId) {
      setVideoId(extractedId);
      setSearchParams({ videoId: extractedId });
    } else {
      setError(
        "Please enter a valid YouTube video URL in the format: https://www.youtube.com/watch?v=VIDEO_ID"
      );
    }
  };

  const handleFetchVideo = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const functions = new Functions();
      const result = await functions.fetchVideo({
        userId: user?.id || "guest",
        videoId: id,
      });

      if (result.data.video) {
        setVideo(result.data.video);
      } else {
        setError("Video not found or we couldn't access this video.");
      }
    } catch (error) {
      console.error("Error fetching video details:", error);
      setError(
        "An error occurred while fetching video details. Please make sure the video ID is correct."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSentimentAnalysis = async () => {
    if (!videoId) return;

    try {
      setAnalyzingSentiment(true);
      setSentimentData(null); // Clear any previous analysis

      const functions = new Functions();
      const result = await functions.analyzeCommentsSentiment({
        videoId: videoId,
      });

      if (result.data && result.data.sentiment) {
        setSentimentData(result.data.sentiment);
      }
    } catch (error) {
      console.error("Error analyzing sentiment:", error);
      setError(
        "Failed to analyze sentiment. Try signing in for full features."
      );
    } finally {
      setAnalyzingSentiment(false);
    }
  };

  const handleAudienceInsights = async () => {
    if (!videoId) return;

    try {
      setAnalyzingAudience(true);
      setAudienceInsights(null);

      // Use a fixed prompt for the free tool
      const fixedPrompt =
        "Brainstorm up to 3 video ideas based on the comments — what did they love or ask for?";

      const functions = new Functions();
      const result = await functions.analyzeVideoComments({
        videoId: videoId,
        prompt: fixedPrompt,
      });

      if (result.data && result.data.analysis) {
        setAudienceInsights(result.data.analysis);
      }
    } catch (error) {
      console.error("Error analyzing audience insights:", error);
      setError(
        "Failed to analyze audience insights. Try signing in for full features."
      );
    } finally {
      setAnalyzingAudience(false);
    }
  };

  const handleLeadSubmission = async (email: string) => {
    try {
      const leadDb = new LeadDb();
      const leadsRef = leadDb.collection({ leadId: "" });
      // Query to check if this email is already saved.
      const q = query(leadsRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        // If the email exists, mark it as submitted without saving a duplicate.
        setLeadSubmitted(true);
        return;
      }
      const lead = new Lead({
        id: "",
        email,
        createdAt: moment(),
      });
      await addDoc(leadsRef, lead);
      setLeadSubmitted(true);
      setError(null);
    } catch (error) {
      console.error("Error saving lead:", error);
      setError("Failed to save your email. Please try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="min-h-screen bg-[#0B0B0F] text-white font-custom">
        <header className="w-full max-w-5xl flex justify-between items-center px-4 py-4 mx-auto">
          <a href="/">
            <img
              src="/images/logo.svg"
              alt="YouGenie Logo"
              className="h-6 sm:h-8 cursor-pointer"
            />
          </a>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-4xl font-bold mb-4">
            YouTube Video Insighter
          </h1>
          <p className="text-gray-400 mb-8">
            Get detailed analytics and insights for any YouTube video.
            Understand audience sentiment, discover what viewers are saying, and
            gain valuable insights about the content.
          </p>

          {/* Video URL Input Section */}
          <div className="bg-[#1A1A1A] rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Analyze a YouTube Video</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="Enter YouTube video URL (e.g., https://www.youtube.com/watch?v=...)"
                className="flex-1 p-2 rounded-lg bg-background border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <CustomButton
                onClick={handleUrlSubmit}
                disabled={loading}
                startIcon={<IconSearch size={16} />}
              >
                Analyze Video
              </CustomButton>
            </div>
            {error && <p className="text-error mt-2">{error}</p>}
          </div>

          {/* Video Details and Analysis Section */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <CircularProgress color="inherit" size={40} />
            </div>
          ) : video ? (
            <div className="bg-[#1A1A1A] rounded-lg p-6">
              {/* Video Details */}
              <VideoDetailsCard video={video} />

              {/* Email Collection or Analysis Panels */}
              {!leadSubmitted ? (
                <div className="mt-8 p-6 border border-gray-700 rounded-lg">
                  <h3 className="text-xl font-bold mb-4">
                    Get Detailed Video Analysis
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Enter your email to unlock sentiment analysis and audience
                    insights for this video.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="email"
                      value={leadEmailInput}
                      onChange={(e) => setLeadEmailInput(e.target.value)}
                      placeholder="Enter your email address"
                      className="flex-1 p-2 rounded-lg bg-background border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <CustomButton
                      onClick={() => {
                        if (!leadEmailInput || !leadEmailInput.includes("@")) {
                          setError("Please enter a valid email address");
                          return;
                        }
                        handleLeadSubmission(leadEmailInput);
                      }}
                      startIcon={<IconMail size={16} />}
                    >
                      Get Analysis
                    </CustomButton>
                  </div>
                  {error && <p className="text-error mt-2">{error}</p>}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-8 mt-8">
                  {/* Sentiment Analysis */}
                  <SentimentAnalysisPanel
                    analyzing={analyzingSentiment}
                    sentimentData={sentimentData}
                    onAnalyze={handleSentimentAnalysis}
                  />

                  {/* Audience Insights */}
                  <AudienceInsightsPanel
                    analyzing={analyzingAudience}
                    insights={audienceInsights}
                    onAnalyze={handleAudienceInsights}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#1A1A1A] rounded-lg p-6 text-center py-12">
              <IconBrandYoutube
                size={48}
                className="mx-auto mb-4 text-red-500"
              />
              <h3 className="text-xl font-bold mb-2">
                Enter a YouTube Video URL to get started
              </h3>
              <p className="text-gray-400">
                Analyze any YouTube video to discover insights about viewer
                sentiment and engagement.
              </p>
            </div>
          )}

          {/* SEO Sections */}
          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">
              Benefits of Using Video Insighter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Understand Your Audience
                </h3>
                <p className="text-gray-400">
                  Get insights into how viewers feel about the video content
                  through sentiment analysis of comments. Discover the emotional
                  response your content creates.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Track Performance Metrics
                </h3>
                <p className="text-gray-400">
                  Access key metrics like views, likes, comments, and engagement
                  rates all in one place without switching between different
                  YouTube analytics tools.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Improve Content Strategy
                </h3>
                <p className="text-gray-400">
                  Use data-driven insights to understand what your audience
                  enjoys, what triggers engagement, and how to optimize your
                  future video content.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">
              How to Use Video Insighter
            </h2>
            <div className="bg-[#1A1A1A] p-6 rounded-lg">
              <ol className="space-y-4">
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    1
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">
                      Enter a YouTube Video URL
                    </h3>
                    <p className="text-gray-400">
                      Copy and paste the URL of any public YouTube video you
                      want to analyze.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    2
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">
                      View Basic Video Metrics
                    </h3>
                    <p className="text-gray-400">
                      See the video's thumbnail, title, views, likes, comments,
                      and publication date at a glance.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    3
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">
                      Run Comment Sentiment Analysis
                    </h3>
                    <p className="text-gray-400">
                      Click the "Analyze Sentiment" button to see how viewers
                      feel about the video.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    4
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">Get Audience Insights</h3>
                    <p className="text-gray-400">
                      Click "Generate Insights" to have AI summarize what
                      viewers are saying in the comments.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">Common Use Cases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Content Creators</h3>
                <p className="text-gray-400">
                  Understand what resonates with your audience by analyzing how
                  they respond to your videos. Use insights to guide your
                  content creation strategy and improve engagement.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Marketing Teams</h3>
                <p className="text-gray-400">
                  Evaluate campaign videos to determine audience response and
                  effectiveness. Compare competitor videos to understand what
                  works in your niche.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Researchers</h3>
                <p className="text-gray-400">
                  Analyze YouTube videos for academic or market research
                  purposes. Study audience responses to various types of
                  content.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Brands and Businesses
                </h3>
                <p className="text-gray-400">
                  Monitor how your brand videos are performing and how audiences
                  perceive your content. Track sentiment across multiple videos
                  to identify trends.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16 mb-16">
            <h2 className="text-3xl font-bold mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  Is Video Insighter free to use?
                </h3>
                <p className="text-gray-400">
                  Yes, the basic version of Video Insighter is completely free
                  to use. For more advanced features and unlimited analysis,
                  consider signing up for our premium plan.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  Can I analyze any YouTube video?
                </h3>
                <p className="text-gray-400">
                  You can analyze any public YouTube video that allows API
                  access. Some videos with restricted comments or other
                  limitations may have reduced analysis capabilities.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  How accurate is the sentiment analysis?
                </h3>
                <p className="text-gray-400">
                  Our sentiment analysis uses advanced AI to determine the
                  emotional tone of comments with high accuracy. However,
                  sarcasm, cultural references, and nuanced expressions may
                  occasionally be misinterpreted.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">Is my data secure?</h3>
                <p className="text-gray-400">
                  Yes, we do not store the video data or analysis results for
                  guest users. All processing is done in real-time and your
                  analysis data is never shared with third parties.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
